import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Lock, Loader2, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { AISparkles } from "@/components/ui/ai-sparkles";
import type { AiPrompt, PayloadToken } from "@/types/ai-prompt";
import type { FeatureCategory, FeatureCategoryKey } from "./types";
import { getActiveVersionText } from "./utils";
import { PromptTokenBadges } from "./PromptTokenBadges";
import { CategoryConfigSettingsPanel } from "./CategoryConfigSettingsPanel";
import { ValidatorPage } from "./ValidatorPage";

type FeatureCategoryViewProps = {
  activeCategory: FeatureCategory;
  categoryPrompts: AiPrompt[];
  isLoading: boolean;
  selectedPromptId: number | null;
  payloadTokens: PayloadToken[];
  onSelectPrompt: (id: number | null) => void;
  focusValidatorFeature?: string | null;
};

export function FeatureCategoryView({
  activeCategory,
  categoryPrompts,
  isLoading,
  selectedPromptId,
  payloadTokens,
  onSelectPrompt,
  focusValidatorFeature = null,
}: FeatureCategoryViewProps) {
  return (
    <div className="min-w-0 lg:px-6 lg:border-r lg:border-border pt-6 lg:pt-0 space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <activeCategory.icon className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold text-foreground">{activeCategory.label}</h2>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{activeCategory.description}</p>
      </div>

      {activeCategory.configSettings && (
        <CategoryConfigSettingsPanel configSettings={activeCategory.configSettings} />
      )}

      {activeCategory.key !== "shared-validator" && (
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Generation Prompts
        </h3>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : categoryPrompts.length === 0 ? (
          <div className="p-4 rounded-lg border border-dashed border-border text-center">
            <AISparkles className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No prompts configured for this feature yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {categoryPrompts.map((prompt) => {
              const isActive = prompt.id === selectedPromptId;
              const activeVer = prompt.versions.find((v) => v.is_active);
              const activeText = getActiveVersionText(prompt);
              return (
                <button
                  key={prompt.id}
                  type="button"
                  onClick={() => onSelectPrompt(isActive ? null : prompt.id)}
                  className={cn(
                    "relative w-full text-left p-3 rounded-lg border transition-colors",
                    isActive
                      ? "border-primary bg-primary-tint-subtle"
                      : "border-border hover:border-primary/30 hover:bg-muted/30"
                  )}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <h4 className="text-base font-semibold text-foreground">{prompt.label}</h4>
                    {activeVer ? (
                      <Badge variant="secondary" className="text-xs">{activeVer.name}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">Default</Badge>
                    )}
                  </div>
                  {prompt.description && (
                    <p className="text-sm text-muted-foreground">{prompt.description}</p>
                  )}
                  {prompt.trigger_label && (
                    <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border/60">
                      <span className="font-medium">Trigger:</span>{" "}
                      <span className="text-foreground">{prompt.trigger_label}</span>
                    </p>
                  )}
                  <PromptTokenBadges promptText={activeText} payloadTokens={payloadTokens} />
                  {isActive && (
                    <span
                      aria-hidden
                      className="hidden lg:flex absolute top-1/2 -right-[13px] -translate-y-1/2 h-6 w-6 items-center justify-center rounded-full border border-primary bg-primary text-primary-foreground shadow-sm"
                    >
                      <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}

      {activeCategory.key === "shared-validator" && (
        <ValidatorPage focusFeature={focusValidatorFeature} />
      )}

      {activeCategory.backendAis && activeCategory.backendAis.length > 0 && (
        <>
          <Separator />
          <div>
            <div className="mb-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Backend AIs
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                These supporting AIs run on this feature but are managed by the backend and not user-configurable.
              </p>
            </div>
            <div className="space-y-1">
              {activeCategory.backendAis.map((b) => (
                <div key={b.name} className="flex items-start gap-2.5 py-1.5">
                  <Lock className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-foreground">{b.name}</p>
                      <Badge variant="outline" className="text-xs">{b.trigger}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{b.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

